import { cardColors } from '@juvare/common-web-ui/Cards/cardColors';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import ColorizeOutlinedIcon from '@mui/icons-material/ColorizeOutlined';
import { Box, FormControl, FormLabel, Typography } from '@mui/material';
import { brown, deepPurple, teal } from '@mui/material/colors';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { RadioItemPicker } from '../RadioItemPicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
let showDeprecatedColors = false;
let convertDeprecatedColorsToSupportedColors = true;
/**
 * Sets whether deprecated colors should be displayed in the application.
 *
 * @param {boolean} flag - If `true`, deprecated colors will be shown; if `false`, they will be hidden.
 * @returns {void}
 */
export function setShowDeprecatedColors(flag) {
    showDeprecatedColors = flag;
}
/**
 * Sets whether deprecated colors should be automatically converted to supported colors.
 *
 * @param {boolean} flag - If `true`, deprecated colors will be converted to supported ones; if `false`, they will remain unchanged.
 * @returns {void}
 */
export function setConvertDeprecatedColorsToSupportedColors(flag) {
    convertDeprecatedColorsToSupportedColors = flag;
}
const WidgetFormColorPicker = (props) => {
    var _a, _b;
    const value = props.getDataStringOrUndefined();
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error },
        props.config.label && (React.createElement(FormLabel, { sx: { mb: 1 } }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(RadioItemPicker, { items: showDeprecatedColors ? colorItemsIncludingDeprecatedColors : colorItems, selectedItemValue: value, onSelectItem: (item) => {
                props.setDataStringOrUndefined(item === null || item === void 0 ? void 0 : item.value);
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, autoFocus: (_b = (!props.editMode && props.config.autoFocus)) !== null && _b !== void 0 ? _b : false, ariaDescribedBy: ariaDescribedBy }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormColorPickerEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetColorPicker.colorPicker') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'color',
                    label: t('widget.widgetColorPicker.color'),
                    style: {
                        inline: true,
                    },
                    event: {
                        onChange: (value, handler) => {
                            const defaultValue = handler.up().down('defaultValue');
                            if (defaultValue) {
                                defaultValue.setData(undefined);
                                defaultValue.config.color = value;
                                defaultValue.update();
                            }
                        },
                    },
                },
                {
                    type: 'form.checkbox',
                    key: 'setDefaultValue',
                    label: t('widget.widgetForm.defaultValue'),
                },
                {
                    type: 'form.colorPicker',
                    key: 'defaultValue',
                    validate: {
                        required: true,
                    },
                    hidden: {
                        when: '^setDefaultValue',
                        dataNotEq: true,
                    },
                    clearDataOnHide: true,
                },
            ],
            onLoad: (data) => {
                if (data.defaultValue) {
                    data.setDefaultValue = true;
                }
                return data;
            },
            onSubmit: (data) => {
                delete data.setDefaultValue;
                return data;
            },
        }), buildValidateTab(props, {
            required: true,
        }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormColorPicker, Object.assign({}, props))));
};
export const widgetFormColorPicker = {
    type,
    init: (props) => {
        if (props.getDataStringOrUndefined() == null && props.config.defaultValue != null) {
            props.setDataStringOrUndefined(props.config.defaultValue);
        }
        if (convertDeprecatedColorsToSupportedColors) {
            const value = props.getDataStringOrUndefined();
            if (value) {
                const convertedValue = convertDeprecatedColorMap[value];
                if (convertedValue) {
                    props.setDataString(convertedValue);
                }
            }
        }
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && value == null) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a;
        const value = props.getDataStringOrUndefined();
        return getLabelAndValuePairs(joinLabels(labelPrefix, (_a = props.config.label) !== null && _a !== void 0 ? _a : 'Color Picker'), value);
    },
    component: WidgetFormColorPicker,
    icon: React.createElement(ColorizeOutlinedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormColorPickerEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'colorPicker', label: 'Color Picker' }),
};
const ColorBox = ({ color, label }) => {
    return (React.createElement(Box, { sx: { gap: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', p: 1, width: 128, height: 24 } },
        React.createElement(Box, { sx: { borderRadius: 8, backgroundColor: color, width: 20, height: 20 } }),
        React.createElement(Typography, null, label)));
};
// prettier-ignore
const colorItems = [
    { value: 'red', element: React.createElement(ColorBox, { color: cardColors['red'], label: 'Red' }), 'aria-label': 'Red' },
    { value: 'pink', element: React.createElement(ColorBox, { color: cardColors['pink'], label: 'Pink' }), 'aria-label': 'Pink' },
    { value: 'purple', element: React.createElement(ColorBox, { color: cardColors['purple'], label: 'Purple' }), 'aria-label': 'Purple' },
    { value: 'blue', element: React.createElement(ColorBox, { color: cardColors['blue'], label: 'Blue' }), 'aria-label': 'Blue' },
    { value: 'cyan', element: React.createElement(ColorBox, { color: cardColors['cyan'], label: 'Cyan' }), 'aria-label': 'Cyan' },
    { value: 'green', element: React.createElement(ColorBox, { color: cardColors['green'], label: 'Green' }), 'aria-label': 'Green' },
    { value: 'orange', element: React.createElement(ColorBox, { color: cardColors['orange'], label: 'Orange' }), 'aria-label': 'Orange' },
];
// prettier-ignore
const colorItemsIncludingDeprecatedColors = [
    ...colorItems,
    { value: 'deepPurple', element: React.createElement(ColorBox, { color: deepPurple[500], label: 'Deep Purple' }), 'aria-label': 'Deep Purple' },
    { value: 'teal', element: React.createElement(ColorBox, { color: teal[500], label: 'Teal' }), 'aria-label': 'Teal' },
    { value: 'yellow', element: React.createElement(ColorBox, { color: '#FFC107', label: 'Yellow' }), 'aria-label': 'Yellow' },
    { value: 'brown', element: React.createElement(ColorBox, { color: brown[500], label: 'Brown' }), 'aria-label': 'Brown' },
    { value: 'grey', element: React.createElement(ColorBox, { color: themeColors.accent.grey.base, label: 'Gray' }), 'aria-label': 'Gray' },
    { value: 'black', element: React.createElement(ColorBox, { color: themeColors.decorative.black, label: 'Black' }), 'aria-label': 'Black' },
];
const convertDeprecatedColorMap = {
    deepPurple: 'purple',
    teal: 'cyan',
    yellow: 'orange',
    brown: 'orange',
    grey: 'blue',
    black: 'blue',
};
